(() => {
  const SELECTOR = '[data-community-stories]';

  class CommunityStories {
    constructor(root) {
      if (!root || root.dataset.csReady === 'true') return;

      root.dataset.csReady = 'true';

      this.root = root;
      this.cards = [...root.querySelectorAll('[data-story-card]')];
      this.track = root.querySelector('.cs-track');
      this.viewer = root.querySelector('[data-story-viewer]');
      this.video = root.querySelector('[data-viewer-video]');
      this.progress = root.querySelector('[data-progress]');

      this.index = 0;
      this.raf = null;
      this.touchX = 0;

      if (!this.viewer || !this.video || !this.progress) return;

      this.bind();
      this.centerDesktopTrack();
      this.updateSoundIcon();
    }

    /* =========================================================
       EVENTS
    ========================================================= */

    bind() {
      this.cards.forEach((card, i) => {
        const openButton = card.querySelector('[data-open-story]');

        openButton?.addEventListener('click', () => {
          this.open(i);
        });
      });

      /* CLOSE */

      this.viewer
        .querySelector('[data-close]')
        ?.addEventListener('click', () => {
          this.close();
        });

      /* NEXT */

      this.viewer
        .querySelector('[data-next]')
        ?.addEventListener('click', () => {
          this.next();
        });

      /* PREVIOUS */

      this.viewer
        .querySelector('[data-prev]')
        ?.addEventListener('click', () => {
          this.prev();
        });

      /* SOUND */

      this.viewer
        .querySelector('[data-sound]')
        ?.addEventListener('click', () => {
          this.toggleSound();
        });

      /* VIDEO FINISHED */

      this.video.addEventListener('ended', () => {
        this.next(true);
      });

      /* VIDEO STARTED */

      this.video.addEventListener('play', () => {
        this.animateProgress();
      });

      /* VIDEO PAUSED */

      this.video.addEventListener('pause', () => {
        cancelAnimationFrame(this.raf);
      });

      /* SOUND CHANGED */

      this.video.addEventListener('volumechange', () => {
        this.updateSoundIcon();
      });

      /* =======================================================
         MOBILE SWIPE
      ======================================================= */

      this.viewer.addEventListener(
        'touchstart',
        (event) => {
          this.touchX = event.changedTouches[0].clientX;
        },
        {
          passive: true
        }
      );

      this.viewer.addEventListener(
        'touchend',
        (event) => {
          const difference =
            event.changedTouches[0].clientX - this.touchX;

          if (Math.abs(difference) > 55) {
            if (difference < 0) {
              this.next();
            } else {
              this.prev();
            }
          }
        },
        {
          passive: true
        }
      );

      /* =======================================================
         KEYBOARD
      ======================================================= */

      document.addEventListener('keydown', (event) => {
        if (this.viewer.hidden) return;

        if (event.key === 'Escape') {
          this.close();
        }

        if (event.key === 'ArrowRight') {
          this.next();
        }

        if (event.key === 'ArrowLeft') {
          this.prev();
        }
      });

      /* =======================================================
         WINDOW RESIZE
      ======================================================= */

      window.addEventListener('resize', () => {
        this.centerDesktopTrack();
      });
    }

    /* =========================================================
       GET STORY DATA
    ========================================================= */

    story(i) {
      return this.cards[i]?.dataset || {};
    }

    /* =========================================================
       CENTER DESKTOP STORIES
    ========================================================= */

    centerDesktopTrack() {
      if (
        !this.track ||
        window.innerWidth < 750 ||
        this.cards.length < 2
      ) {
        return;
      }

      requestAnimationFrame(() => {
        const middleIndex =
          Math.floor((this.cards.length - 1) / 2);

        const middleCard = this.cards[middleIndex];

        if (!middleCard) return;

        this.cards.forEach((card) => {
          card.classList.remove('cs-card-centered');
        });

        middleCard.classList.add('cs-card-centered');

        const left =
          middleCard.offsetLeft -
          (
            this.track.clientWidth -
            middleCard.offsetWidth
          ) / 2;

        this.track.scrollTo({
          left: Math.max(0, left),
          behavior: 'auto'
        });
      });
    }

    /* =========================================================
       OPEN STORY
    ========================================================= */

    open(i) {
      this.index = i;

      this.viewer.hidden = false;

      document.body.classList.add('cs-lock');

      this.renderProgress();

      this.video.muted = false;
      this.video.volume = 1;

      this.updateSoundIcon();

      this.load(i, true);
    }

    /* =========================================================
       CLOSE STORY
    ========================================================= */

    close() {
      this.viewer.hidden = true;

      document.body.classList.remove('cs-lock');

      cancelAnimationFrame(this.raf);

      this.video.pause();

      this.video.removeAttribute('src');

      this.video.load();
    }

    /* =========================================================
       INSTAGRAM STYLE PROGRESS BARS
    ========================================================= */

    renderProgress() {
      this.progress.innerHTML = this.cards
        .map(
          (_, i) => `
            <div class="cs-progress-item">
              <div
                class="cs-progress-fill"
                data-fill="${i}"
              ></div>
            </div>
          `
        )
        .join('');
    }

    /* =========================================================
       LOAD STORY
    ========================================================= */

    async load(i, preferSound = false) {
      if (!this.cards.length) return;

      this.index =
        (i + this.cards.length) %
        this.cards.length;

      const story = this.story(this.index);

      cancelAnimationFrame(this.raf);

      /* RESET PROGRESS */

      [
        ...this.progress.querySelectorAll(
          '.cs-progress-fill'
        )
      ].forEach((fill, number) => {
        fill.style.width =
          number < this.index
            ? '100%'
            : '0%';
      });

      /* STOP PREVIOUS VIDEO */

      this.video.pause();
      this.video.removeAttribute('src');

      /* LOAD ACTIVE VIDEO */

      if (story.video) {
        this.video.src = story.video;
      }

      /* POSTER */

      this.video.poster = story.poster || '';

      /* SOUND */

      if (preferSound) {
        this.video.muted = false;
        this.video.volume = 1;
      }

      this.updateSoundIcon();

      /* PRODUCT IMAGE */

      const productImage =
        this.viewer.querySelector(
          '[data-product-image]'
        );

      if (productImage) {
        if (story.productImage) {
          productImage.src = story.productImage;
          productImage.hidden = false;
        } else {
          productImage.removeAttribute('src');
          productImage.hidden = true;
        }
      }

      /* PRODUCT TITLE */

      const productTitle =
        this.viewer.querySelector(
          '[data-product-title]'
        );

      if (productTitle) {
        productTitle.textContent =
          story.productTitle || '';
      }

      /* PRODUCT PRICE */

      const productPrice =
        this.viewer.querySelector(
          '[data-product-price]'
        );

      if (productPrice) {
        productPrice.textContent =
          story.productPrice || '';
      }

      /* DESCRIPTION */

      const productDescription =
        this.viewer.querySelector(
          '[data-product-description]'
        );

      if (productDescription) {
        productDescription.innerHTML =
          story.description || '';
      }

      /* =======================================================
         SHOP NOW BUTTON
      ======================================================= */

      const shopButton =
        this.viewer.querySelector(
          '[data-shop-now]'
        );

      if (shopButton) {
        shopButton.textContent =
          story.shopText ||
          'Shop now';

        shopButton.href =
          story.productUrl ||
          '#';

        shopButton.hidden =
          !story.productUrl ||
          story.showShop === 'false';
      }

      /* =======================================================
         LOAD VIDEO
      ======================================================= */

      this.video.load();

      /* =======================================================
         AUTOPLAY WITH SOUND
      ======================================================= */

      try {
        await this.video.play();
      } catch (error) {
        this.video.muted = true;

        this.updateSoundIcon();

        try {
          await this.video.play();
        } catch (_) {}
      }
    }

    /* =========================================================
       NEXT STORY
    ========================================================= */

    next(fromEnded = false) {
      if (
        fromEnded &&
        this.index === this.cards.length - 1 &&
        this.root.dataset.loop !== 'true'
      ) {
        return this.close();
      }

      this.load(
        this.index + 1,
        false
      );
    }

    /* =========================================================
       PREVIOUS STORY
    ========================================================= */

    prev() {
      this.load(
        this.index - 1,
        false
      );
    }

    /* =========================================================
       SOUND TOGGLE
    ========================================================= */

    toggleSound() {
      this.video.muted =
        !this.video.muted;

      if (!this.video.muted) {
        this.video.volume = 1;
      }

      this.updateSoundIcon();

      if (this.video.paused) {
        this.video
          .play()
          .catch(() => {});
      }
    }

    /* =========================================================
       SVG SOUND ICON STATE
    ========================================================= */

    updateSoundIcon() {
      const button =
        this.viewer?.querySelector(
          '[data-sound]'
        );

      if (!button) return;

      const muted =
        this.video.muted ||
        this.video.volume === 0;

      const onIcon =
        button.querySelector(
          '.cs-sound-on'
        );

      const offIcon =
        button.querySelector(
          '.cs-sound-off'
        );

      if (onIcon) {
        onIcon.hidden = muted;
      }

      if (offIcon) {
        offIcon.hidden = !muted;
      }

      button.setAttribute(
        'aria-label',
        muted
          ? 'Turn sound on'
          : 'Mute video'
      );

      button.setAttribute(
        'aria-pressed',
        muted
          ? 'true'
          : 'false'
      );
    }

    /* =========================================================
       VIDEO PROGRESS
    ========================================================= */

    animateProgress() {
      cancelAnimationFrame(this.raf);

      const tick = () => {
        const fill =
          this.progress.querySelector(
            `[data-fill="${this.index}"]`
          );

        if (
          fill &&
          Number.isFinite(this.video.duration) &&
          this.video.duration > 0
        ) {
          const percentage =
            (
              this.video.currentTime /
              this.video.duration
            ) * 100;

          fill.style.width =
            `${Math.min(
              100,
              percentage
            )}%`;
        }

        if (
          !this.video.paused &&
          !this.video.ended
        ) {
          this.raf =
            requestAnimationFrame(tick);
        }
      };

      tick();
    }
  }

  /* ===========================================================
     INITIALIZE
  =========================================================== */

  const init = (scope = document) => {
    if (scope.matches?.(SELECTOR)) {
      new CommunityStories(scope);
    }

    scope
      .querySelectorAll?.(SELECTOR)
      .forEach((element) => {
        new CommunityStories(element);
      });
  };

  /* ===========================================================
     NORMAL PAGE LOAD
  =========================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        init();
      }
    );
  } else {
    init();
  }

  /* ===========================================================
     SHOPIFY THEME EDITOR
  =========================================================== */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(event.target);
    }
  );
})();